/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { FileUp } from "lucide-react";
import { useDropzone } from "react-dropzone";

interface UploadZoneProps {
  loading: boolean;
  error: string | null;
  onDrop: (acceptedFiles: File[]) => Promise<void>;
}

export function UploadZone({ loading, error, onDrop }: UploadZoneProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/zip": [".zip"] },
    multiple: false,
  });

  return (
    <div className="mx-auto mb-12 w-full max-w-xl text-center">
      <div
        {...getRootProps()}
        className={cn(
          "relative flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/5",
        )}
      >
        <input {...getInputProps()} />
        <FileUp className="text-muted-foreground mb-4 h-8 w-8" />
        <p className="mb-2 text-sm font-medium">Upload Discord Data Package</p>
        <p className="text-muted-foreground text-sm">
          {isDragActive
            ? "Drop to upload..."
            : "Drag and drop your ZIP file here, or click to select"}
        </p>
      </div>

      {loading && (
        <div className="mt-4 text-center">
          <p className="text-muted-foreground text-sm">Processing...</p>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
